import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ============== SAFE DATA HELPERS ==============

function safeNumber(value, defaultVal = 0) {
  if (value === null || value === undefined) return defaultVal;
  const num = Number(value);
  return isNaN(num) ? defaultVal : num;
}

function safeString(value, defaultVal = '') {
  if (value === null || value === undefined) return defaultVal;
  return String(value);
}

function safeArray(value) {
  if (Array.isArray(value)) return value;
  return [];
}

function safeObject(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  return {};
}

// ============== STATUS BADGES ==============

function StatusBadge({ status }) {
  const colors = {
    "Job Created": "bg-slate-500",
    "Awaiting Quote": "bg-amber-500",
    "Quote Awaiting Approval": "bg-orange-500",
    "Approved": "bg-purple-600",
    "Assigned": "bg-sky-500",
    "Scheduled": "bg-blue-600",
    "In Progress": "bg-indigo-600",
    "Completed": "bg-green-600",
    "Closed": "bg-slate-700",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white ${colors[status] || "bg-slate-600"}`}>
      {safeString(status, 'Unknown')}
    </span>
  );
}

function ComplianceStatusBadge({ status }) {
  const styles = {
    valid: "bg-green-100 text-green-800 border-green-200",
    due: "bg-amber-100 text-amber-800 border-amber-200",
    critical: "bg-orange-100 text-orange-800 border-orange-200",
    expired: "bg-red-100 text-red-800 border-red-200",
    unknown: "bg-slate-100 text-slate-800 border-slate-200",
  };
  const icons = {
    valid: "✓",
    due: "!",
    critical: "!!",
    expired: "✕",
    unknown: "?",
  };
  const safeStatus = safeString(status, 'unknown');
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${styles[safeStatus] || styles.unknown}`}>
      <span>{icons[safeStatus] || icons.unknown}</span>
      {safeStatus}
    </span>
  );
}

// ============== SUMMARY CARD ==============

function SummaryCard({ title, value, subtitle, color = "teal", icon }) {
  const bgColors = {
    teal: "bg-teal-50 border-teal-200",
    amber: "bg-amber-50 border-amber-200",
    red: "bg-red-50 border-red-200",
    green: "bg-green-50 border-green-200",
    blue: "bg-blue-50 border-blue-200",
  };
  const textColors = {
    teal: "text-teal-700",
    amber: "text-amber-700",
    red: "text-red-700",
    green: "text-green-700",
    blue: "text-blue-700",
  };
  return (
    <div className={`rounded-xl border p-4 ${bgColors[color] || bgColors.teal}`} data-testid={`summary-card-${title?.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-600">{safeString(title)}</p>
          <p className={`text-2xl font-bold ${textColors[color] || textColors.teal}`}>{safeNumber(value)}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{safeString(subtitle)}</p>}
        </div>
        {icon && <div className={`text-3xl ${textColors[color] || textColors.teal}`}>{icon}</div>}
      </div>
    </div>
  );
}

// ============== COMPLIANCE TRAFFIC LIGHT ==============

function ComplianceTrafficLight({ valid, due, expired }) {
  return (
    <div className="flex items-center gap-4 flex-wrap">
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 rounded-full bg-green-500"></div>
        <span className="text-sm">{safeNumber(valid)} Valid</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 rounded-full bg-amber-500"></div>
        <span className="text-sm">{safeNumber(due)} Due</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 rounded-full bg-red-500"></div>
        <span className="text-sm">{safeNumber(expired)} Expired</span>
      </div>
    </div>
  );
}

// ============== MAIN COMPONENT ==============

export default function ClientDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [jobs, setJobs] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [documents, setDocuments] = useState([]);

  // Use the new billing API endpoints
  const API_BASE = `${API}/api/client-billing`;

  // Get fresh headers with current token
  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API_BASE}/dashboard`, { headers: getHeaders() });
      const data = safeObject(res.data);
      if (data.ok) {
        setDashboard(data);
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
      } else {
        const errorMsg = err.response?.data?.detail || 'Failed to load dashboard';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  }, [API_BASE, navigate]);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/jobs`, { headers: getHeaders() });
      const data = safeObject(res.data);
      if (data.ok) {
        setJobs(safeArray(data.jobs));
      }
    } catch (err) {
      console.error('Failed to fetch jobs', err);
      setJobs([]);
    }
  }, [API_BASE]);

  const fetchCompliance = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/compliance`, { headers: getHeaders() });
      const data = safeObject(res.data);
      if (data.ok) {
        setCompliance(data);
      }
    } catch (err) {
      console.error('Failed to fetch compliance', err);
      setCompliance({ summary: { total: 0, valid: 0, due: 0, critical: 0, expired: 0 }, items: [] });
    }
  }, [API_BASE]);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/invoices`, { headers: getHeaders() });
      const data = safeObject(res.data);
      if (data.ok) {
        setInvoices(safeArray(data.invoices));
      }
    } catch (err) {
      console.error('Failed to fetch invoices', err);
      setInvoices([]);
    }
  }, [API_BASE]);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/documents`, { headers: getHeaders() });
      const data = safeObject(res.data);
      if (data.ok) {
        setDocuments(safeArray(data.documents));
      }
    } catch (err) {
      console.error('Failed to fetch documents', err);
      setDocuments([]);
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (activeTab === 'jobs') fetchJobs();
    if (activeTab === 'compliance') fetchCompliance();
    if (activeTab === 'invoices') fetchInvoices();
    if (activeTab === 'documents') fetchDocuments();
  }, [activeTab, fetchJobs, fetchCompliance, fetchInvoices, fetchDocuments]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error && !dashboard) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4" data-testid="error-state">
        <p className="text-slate-500">{error}</p>
        <Button onClick={fetchDashboard} variant="outline">Retry</Button>
      </div>
    );
  }

  // Safe data extraction with defaults
  const client = safeObject(dashboard?.client);
  const summary = safeObject(dashboard?.summary);
  const complianceData = safeObject(dashboard?.compliance);
  const recentJobs = safeArray(dashboard?.recent_jobs);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_park-case-tracker/artifacts/cww8tyf2_LHS%20LOGO%202%20.png"
                alt="LHS"
                className="h-10"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div>
                <h1 className="text-xl font-bold text-slate-800">Client Portal</h1>
                <p className="text-sm text-slate-500">{safeString(client.name, 'Welcome')}</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => { localStorage.removeItem('token'); navigate('/login'); }} data-testid="logout-btn">
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="tab-jobs">Jobs</TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard
                title="Total Jobs"
                value={summary.total_jobs}
                color="blue"
              />
              <SummaryCard
                title="Active Jobs"
                value={summary.active_jobs}
                color="teal"
              />
              <SummaryCard
                title="Overdue"
                value={summary.overdue_jobs}
                color={safeNumber(summary.overdue_jobs) > 0 ? "amber" : "green"}
              />
              <SummaryCard
                title="Missed Visits"
                value={summary.missed_visits}
                color={safeNumber(summary.missed_visits) > 0 ? "red" : "green"}
              />
            </div>

            {/* Compliance Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                  <span>Compliance Status</span>
                  <ComplianceTrafficLight
                    valid={complianceData.valid}
                    due={complianceData.due}
                    expired={complianceData.expired}
                  />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {safeArray(complianceData.items).length === 0 ? (
                  <p className="text-slate-500 text-sm">No compliance items tracked</p>
                ) : (
                  <div className="space-y-2">
                    {safeArray(complianceData.items).slice(0, 5).map((item, idx) => (
                      <div key={safeString(item.asset_id) || idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                        <div>
                          <p className="font-medium text-sm">{safeString(item.certificate_type, 'Certificate')}</p>
                          <p className="text-xs text-slate-500">{safeString(item.asset_name, 'Unknown Asset')}</p>
                        </div>
                        <div className="text-right">
                          <ComplianceStatusBadge status={item.expiry_status} />
                          <p className="text-xs text-slate-500 mt-1">Due: {safeString(item.next_due_date, 'N/A')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Jobs */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Jobs</CardTitle>
              </CardHeader>
              <CardContent>
                {recentJobs.length === 0 ? (
                  <p className="text-slate-500 text-sm">No jobs yet</p>
                ) : (
                  <div className="space-y-3">
                    {recentJobs.map((job) => (
                      <div
                        key={safeString(job.id)}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                        onClick={() => navigate(`/client/jobs/${safeString(job.id)}`)}
                        data-testid={`job-card-${safeString(job.id)}`}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-slate-500">{safeString(job.job_reference)}</span>
                            <StatusBadge status={job.status} />
                          </div>
                          <p className="font-medium text-sm mt-1">{safeString(job.site_name, 'Unknown Site')}</p>
                          <p className="text-xs text-slate-500">{safeString(job.job_type)}</p>
                        </div>
                        <div className="text-right">
                          {job.scheduled_date && (
                            <p className="text-xs text-slate-500">Scheduled: {safeString(job.scheduled_date)}</p>
                          )}
                          {safeNumber(job.approved_value) > 0 && (
                            <p className="text-sm font-medium text-teal-600">£{safeNumber(job.approved_value).toFixed(2)}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* JOBS TAB */}
          <TabsContent value="jobs">
            <Card>
              <CardHeader>
                <CardTitle>All Jobs</CardTitle>
              </CardHeader>
              <CardContent>
                {jobs.length === 0 ? (
                  <p className="text-slate-500">No jobs found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-2">Reference</th>
                          <th className="text-left py-3 px-2">Site</th>
                          <th className="text-left py-3 px-2">Type</th>
                          <th className="text-left py-3 px-2">Status</th>
                          <th className="text-left py-3 px-2">Contractor</th>
                          <th className="text-left py-3 px-2">Scheduled</th>
                          <th className="text-right py-3 px-2">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {jobs.map((job) => (
                          <tr
                            key={safeString(job.id)}
                            className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                            onClick={() => navigate(`/client/jobs/${safeString(job.id)}`)}
                            data-testid={`jobs-table-row-${safeString(job.id)}`}
                          >
                            <td className="py-3 px-2 font-mono text-xs">{safeString(job.job_reference)}</td>
                            <td className="py-3 px-2">{safeString(job.site_name)}</td>
                            <td className="py-3 px-2">{safeString(job.job_type_label) || safeString(job.job_type)}</td>
                            <td className="py-3 px-2"><StatusBadge status={job.status} /></td>
                            <td className="py-3 px-2">{safeString(job.contractor_name, '-')}</td>
                            <td className="py-3 px-2">{safeString(job.scheduled_date, '-')}</td>
                            <td className="py-3 px-2 text-right">
                              {safeNumber(job.approved_value) > 0 ? `£${safeNumber(job.approved_value).toFixed(2)}` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* COMPLIANCE TAB */}
          <TabsContent value="compliance">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                  <span>Compliance Overview</span>
                  {compliance && (
                    <ComplianceTrafficLight
                      valid={safeObject(compliance.summary).valid}
                      due={safeNumber(safeObject(compliance.summary).due) + safeNumber(safeObject(compliance.summary).critical)}
                      expired={safeObject(compliance.summary).expired}
                    />
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!compliance ? (
                  <p className="text-slate-500">Loading...</p>
                ) : safeArray(compliance.items).length === 0 ? (
                  <p className="text-slate-500">No compliance items tracked</p>
                ) : (
                  <div className="space-y-2">
                    {safeArray(compliance.items).map((item, idx) => (
                      <div
                        key={safeString(item.id) || idx}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          item.status === 'expired' ? 'bg-red-50 border-red-200' :
                          item.status === 'critical' ? 'bg-orange-50 border-orange-200' :
                          item.status === 'due' ? 'bg-amber-50 border-amber-200' :
                          'bg-green-50 border-green-200'
                        }`}
                        data-testid={`compliance-item-${safeString(item.id) || idx}`}
                      >
                        <div>
                          <p className="font-medium">{safeString(item.certificate_type, 'Certificate')}</p>
                          <p className="text-sm text-slate-600">{safeString(item.name)}</p>
                          <p className="text-xs text-slate-500">{safeString(item.site_name)} {item.unit_number && `- ${safeString(item.unit_number)}`}</p>
                        </div>
                        <div className="text-right">
                          <ComplianceStatusBadge status={item.status} />
                          <p className="text-sm mt-1">Due: {safeString(item.next_due_date, 'N/A')}</p>
                          {item.days_remaining !== null && item.days_remaining !== undefined && (
                            <p className={`text-xs ${safeNumber(item.days_remaining) < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                              {safeNumber(item.days_remaining) < 0 ? `${Math.abs(safeNumber(item.days_remaining))} days overdue` : `${safeNumber(item.days_remaining)} days remaining`}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* DOCUMENTS TAB */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle>Documents</CardTitle>
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <p className="text-slate-500">No documents available</p>
                ) : (
                  <div className="space-y-2">
                    {documents.map((doc, idx) => (
                      <div key={safeString(doc.id) || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`document-item-${idx}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center">
                            <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-medium text-sm">{safeString(doc.original_filename) || safeString(doc.document_type, 'Document')}</p>
                            <p className="text-xs text-slate-500">{safeString(doc.job_reference)} • {safeString(doc.document_type)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">{safeString(doc.uploaded_at, '').split('T')[0]}</span>
                          {doc.file_url && (
                            <a
                              href={`${API}${safeString(doc.file_url)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-teal-600 hover:text-teal-700"
                              data-testid={`download-doc-${idx}`}
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* INVOICES TAB */}
          <TabsContent value="invoices">
            <Card>
              <CardHeader>
                <CardTitle>Invoices</CardTitle>
              </CardHeader>
              <CardContent>
                {invoices.length === 0 ? (
                  <p className="text-slate-500">No invoices yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-2">Invoice #</th>
                          <th className="text-left py-3 px-2">Period</th>
                          <th className="text-left py-3 px-2">Jobs</th>
                          <th className="text-right py-3 px-2">Amount</th>
                          <th className="text-left py-3 px-2">Status</th>
                          <th className="text-left py-3 px-2">Due Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((inv, idx) => (
                          <tr key={safeString(inv.id) || idx} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`invoice-row-${idx}`}>
                            <td className="py-3 px-2 font-mono">{safeString(inv.invoice_number)}</td>
                            <td className="py-3 px-2">{safeString(inv.period_start, '').split('T')[0]} - {safeString(inv.period_end, '').split('T')[0]}</td>
                            <td className="py-3 px-2">{safeNumber(inv.jobs_count)}</td>
                            <td className="py-3 px-2 text-right font-medium">£{safeNumber(inv.total_inc_vat).toFixed(2)}</td>
                            <td className="py-3 px-2">
                              <Badge variant={inv.status === 'paid' ? 'default' : inv.status === 'issued' ? 'secondary' : 'outline'}>
                                {safeString(inv.status, 'draft')}
                              </Badge>
                            </td>
                            <td className="py-3 px-2">{safeString(inv.due_date, '').split('T')[0]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
